import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Add default inner padding (default: true) */
  padded?: boolean;
  /** Make the card press/hover-feedback (use only when clickable) */
  interactive?: boolean;
  /** Variant: 'default' (white) | 'subtle' (ink-50) */
  variant?: 'default' | 'subtle';
}

export function Card({
  children,
  padded = true,
  interactive = false,
  variant = 'default',
  className,
  ...rest
}: CardProps) {
  return (
    <div
      className={cn(
        'card',
        padded && 'card-pad',
        variant === 'subtle' && 'bg-ink-50 shadow-none',
        interactive && 'transition duration-150 active:scale-[0.99] hover:shadow-cardLg cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

import { useLanguage } from '@/hooks/useLanguage';

interface CardHeaderProps {
  eyebrow?: string;
  title: ReactNode;
  trailing?: ReactNode;
  description?: ReactNode;
  className?: string;
}

export function CardHeader({ eyebrow, title, trailing, description, className }: CardHeaderProps) {
  const { t } = useLanguage();
  const translateIfString = (val: ReactNode): ReactNode => {
    if (typeof val === 'string') return t(val);
    return val;
  };

  return (
    <header className={cn('flex items-start justify-between gap-3 mb-3', className)}>
      <div className="min-w-0">
        {eyebrow ? <div className="label-eyebrow mb-1">{translateIfString(eyebrow)}</div> : null}
        <h3 className="font-display font-semibold text-ink-900 text-base leading-tight">
          {translateIfString(title)}
        </h3>
        {description ? (
          <p className="text-[11px] text-ink-500 mt-0.5 leading-snug">{translateIfString(description)}</p>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{translateIfString(trailing)}</div> : null}
    </header>
  );
}
